import { FormBuilder } from '@/components/formBuilder';
import { getAtsJobsMutations } from '@/store/atsJobs';
import {
  Button,
  Flex,
  // Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Box,
} from '@chakra-ui/react';
import { useRef, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { useGetAllJobsListQuery } from '@/store/query/ats-jobs';
import { KText } from '@/ui-library/components/text';
import { useShowToast } from '@/hooks/useShowToast';

interface JobCreateEditModalProps {
  /** Chakra useDisclosure handle driving the modal. */
  modalActions?: { isOpen?: boolean; onClose?: () => void; [key: string]: any };
  /** Mode, prefilled job data and callbacks, supplied by the caller. */
  config?: Record<string, any>;
}

export const JobCreateEditModal = ({
  modalActions = {},
  config = {},
}: JobCreateEditModalProps) => {
  const { currentJob = {} } = useSelector(getAtsJobCreate);
  const { data: { name: jobName, id: jobId } = {} } = currentJob;
  const { isOpen, onClose } = modalActions;
  const {
    id: configId,
    label,
    renderType,
    // icon,
    description,
    modalActionText,
    apiCall,
    loadingText,
    initialValues,
    reFetch = () => {},
    skipRedirect = false,
  } = config;

  const formRef = useRef<{ handleSubmit: () => void } | null>(null);
  const toast = useShowToast();
  const history = useHistory();

  const { createJob = {}, updateJob = {} } = useSelector(getAtsJobsMutations);
  const { isLoading, data: { id } = {}, errors } = createJob;
  const {
    isLoading: isUpdateLoading,
    data: { data: { id: updatedId } = {} } = {},
  } = updateJob;
  const { data: ats_jobs = [] } = useGetAllJobsListQuery({
    params: {
      scope: 'viewable',
    },
  });
  const [isActionLoading, setIsActionLoading] = useState(false);

  const renderHeader = () => {
    return (
      <Flex gap="1rem" alignItems="center">
        {/* todo: enable if needed in future
        <Icon as={icon} color="ats-primary" w="2em" h="2em" /> */}
        <Flex flexDir="column" gap="4px">
          <KText size="md-medium" color="secondary-900">
            {label}
          </KText>
          <KText size="sm-medium" color="secondary-700">
            {description}
          </KText>
        </Flex>
      </Flex>
    );
  };

  useEffect(() => {
    if (configId === 'createJob' && id) {
      if (!skipRedirect) {
        toast({
          title: 'Job created successfully',
          position: 'bottom-left',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        history.push(`/jobs/${id}`);
      }
      reFetch();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (errors?.length > 0) {
      toast({
        title: errors[0],
        position: 'bottom-left',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  useEffect(() => {
    if (configId === 'updateJob' && updatedId) {
      toast({
        title: 'Job updated successfully',
        position: 'bottom-left',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      reFetch();
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedId]);

  const handleFormSubmit = async (values: Record<string, unknown>) => {
    apiCall(values, onClose, setIsActionLoading);
  };

  const renderBody = () => {
    const createFormConfig = [
      {
        name: 'name',
        label: 'Internal job name',
        type: 'input',
        isRequired: true,
        maxLength: 255,
      },
      {
        name: 'confidential',
        label: 'Is confidential',
        type: 'checkbox',
        labelInfoText: `Confidential jobs are visible only to hiring team and users with the “Manage all confidential jobs” permission`,
      },
    ];
    const cloneFormConfig = [
      {
        name: 'cloneJobId',
        label: 'Internal Job',
        type: 'select',
        isRequired: true,
        options: ats_jobs,
        labelKey: 'name',
        valueKey: 'id',
      },
    ];

    if (renderType === 'notForm') {
      return (
        <Flex p="0 1em 1em 1.6em">
          <KText fontSize="sm">
            Cloning this job
            <KText as="span" size="sm-semi-bold">
              {' '}
              {jobName}{' '}
            </KText>
            will create a duplicate without any candidates
          </KText>
        </Flex>
      );
    }

    return (
      <Box m=".5em 1.5em">
        <FormBuilder
          config={configId === 'cloneJob' ? cloneFormConfig : createFormConfig}
          onSubmit={handleFormSubmit}
          initialValues={{ confidential: false, ...initialValues }}
          formRef={formRef}
        />
      </Box>
    );
  };

  const renderModal = () => {
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        closeOnOverlayClick={false}
        isCentered
        trapFocus={configId !== 'cloneJob'}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader pb="0">{renderHeader()}</ModalHeader>
          <ModalBody p="0">{renderBody()}</ModalBody>
          <ModalFooter pt="0">
            <Button
              variant="secondary"
              mr={3}
              data-test-id="secondary-cta-button"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              isLoading={isLoading || isUpdateLoading || isActionLoading}
              loadingText={loadingText}
              variant="primary"
              data-test-id="primary-cta-button"
              onClick={() => {
                if (formRef.current) {
                  formRef.current.handleSubmit();
                } else if (renderType === 'notForm') {
                  apiCall(jobId, onClose, setIsActionLoading);
                }
              }}
            >
              {modalActionText}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  };

  return <>{renderModal()}</>;
};
